// Smart Shopping Assistant with DeepSeek Free API
// Handles intent detection, entity extraction, typo correction, and multi-language support

const DEEPSEEK_API_KEY = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_DEEPSEEK_API_KEY) || 
  (typeof process !== 'undefined' && process.env && process.env.VITE_DEEPSEEK_API_KEY) || 
  'sk-or-v1-1f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f8f';

// Typo correction map for common e-commerce terms
const TYPO_MAP = {
  // English
  'iphone': ['iphon', 'iphne', 'ipone', 'iph0ne', 'ifone', 'aphone'],
  'samsung': ['samsun', 'samung', 'samsug', 'sansung'],
  'backpack': ['bagpack', 'bakpack', 'backpak', 'bakcpack'],
  'laptop': ['labtop', 'loptop', 'laptap', 'laptp'],
  'perfume': ['parfume', 'perfum', 'perfume', 'parfum', 'perfuem'],
  'jacket': ['jaket', 'jackit', 'jakcet'],
  'headphones': ['headfones', 'headfons', 'hedphones', 'headphons'],
  'charger': ['charjer', 'chargr', 'charger'],
  'available': ['availible', 'availble', 'avaliable'],
  'order': ['oder', 'ordr', 'ordor'],
  'latest': ['latst', 'laest', 'lates'],
  
  // Portuguese
  'mochila': ['mochilla', 'mochilla', 'moxila', 'mochyla'],
  'jaqueta': ['jaquetta', 'jaketa', 'jacqueta'],
  'perfume': ['perfume', 'perfumme', 'perfumee'],
  'celular': ['celullar', 'selular', 'celulaar'],
  'computador': ['computador', 'comutador', 'computadro'],
  'pedido': ['pediddo', 'pedido', 'peddido'],
  'último': ['ultimo', 'ultim0', 'ultímo'],
  
  // Spanish
  'mochila': ['mochilla', 'mochilla', 'moxila'],
  'chaqueta': ['chaquetta', 'chaketa', 'chacqueta'],
  'teléfono': ['telefono', 'telephono', 'telefoño'],
  'ordenador': ['ordenador', 'ordenadro', 'ordenadr'],
  'pedido': ['pediddo', 'peddido'],
  'último': ['ultimo', 'ultim0']
};

// Build reverse lookup for typos
const TYPO_CORRECTIONS = {};
Object.keys(TYPO_MAP).forEach(correct => {
  TYPO_MAP[correct].forEach(typo => {
    TYPO_CORRECTIONS[typo] = correct;
  });
});

/**
 * Correct typos in user input
 */
export const correctTypos = (text) => {
  if (!text) return text;
  
  const words = text.toLowerCase().split(/\s+/);
  const corrected = words.map(word => {
    // Remove punctuation for matching
    const clean = word.replace(/[.,!?;:]/g, '');
    return TYPO_CORRECTIONS[clean] || word;
  });
  
  return corrected.join(' ');
};

/**
 * Detect language from message
 */
export const detectLanguage = (message) => {
  const text = message.toLowerCase();
  
  // Portuguese indicators
  const ptIndicators = ['não', 'sim', 'obrigado', 'obrigada', 'por favor', 'quero', 'preciso', 
    'busco', 'procurar', 'encontrar', 'comprar', 'tem', 'têm', 'meu', 'minha', 'pedido', 
    'último', 'recente', 'olá', 'oi', 'qual', 'como', 'onde', 'quando'];
  
  // Spanish indicators  
  const esIndicators = ['no', 'sí', 'gracias', 'por favor', 'quiero', 'necesito', 'busco', 
    'buscar', 'encontrar', 'comprar', 'tienes', 'tiene', 'mi', 'pedido', 'último', 
    'reciente', 'hola', 'cuál', 'cómo', 'dónde', 'cuándo'];
  
  // English indicators
  const enIndicators = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 
    'with', 'my', 'order', 'latest', 'recent', 'hello', 'hi', 'hey', 'what', 'how', 'where', 'when'];
  
  const ptCount = ptIndicators.filter(word => text.includes(word)).length;
  const esCount = esIndicators.filter(word => text.includes(word)).length;
  const enCount = enIndicators.filter(word => text.includes(word)).length;
  
  if (ptCount > esCount && ptCount > enCount) return 'pt';
  if (esCount > enCount) return 'es';
  return 'en';
};

/**
 * Call DeepSeek API for intelligent responses
 */
const callDeepSeek = async (messages, systemPrompt) => {
  try {
    const response = await fetch('https://api.deepseek.com/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          { role: 'system', content: systemPrompt },
          ...messages
        ],
        temperature: 0.7,
        max_tokens: 600,
        stream: false
      })
    });
    
    if (!response.ok) {
      console.error('DeepSeek API error:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    if (data.choices && data.choices[0] && data.choices[0].message) {
      return data.choices[0].message.content;
    }
    
    return null;
  } catch (error) {
    console.error('DeepSeek API call failed:', error);
    return null;
  }
};

/**
 * Extract intent and entities using DeepSeek
 * Returns: { intent, entities, quantity, language, confidence }
 */
export const getStructuredIntent = async (message, conversationHistory = []) => {
  const correctedMessage = correctTypos(message);
  const language = detectLanguage(correctedMessage);
  
  // Build conversation context
  const context = conversationHistory.slice(-3).map(msg => 
    `${msg.sender === 'user' ? 'User' : 'Assistant'}: ${msg.text}`
  ).join('\n');
  
  const systemPrompt = `You are an e-commerce intent classifier. Analyze the user's message and return ONLY a valid JSON object.

Available intents:
- "search_product": User wants to find products
- "order_status": User asks about their orders
- "order_latest": User wants their most recent order
- "order_biggest": User wants their order with highest total price
- "recommendation": User wants product recommendations
- "small_talk": Greetings, questions, general conversation
- "help": User needs assistance
- "refuse": Inappropriate or harmful requests

Extract:
- intent (string): one of the above
- entities (object): { products: [], quantities: [], brands: [], categories: [], orderNumbers: [], dates: [] }
- confidence (number): 0.0 to 1.0
- language (string): detected language code (en, pt, es)

Return ONLY valid JSON without any markdown, explanation, or extra text.

Examples:
Input: "Do you have iPhone 16?"
Output: {"intent":"search_product","entities":{"products":["iPhone 16"],"quantities":[],"brands":["Apple"],"categories":["phones"],"orderNumbers":[],"dates":[]},"confidence":0.95,"language":"en"}

Input: "Qual foi meu último pedido?"
Output: {"intent":"order_latest","entities":{"products":[],"quantities":[],"brands":[],"categories":[],"orderNumbers":[],"dates":[]},"confidence":0.98,"language":"pt"}

Input: "I ordered 4 backpacks"
Output: {"intent":"order_status","entities":{"products":["backpack"],"quantities":[4],"brands":[],"categories":["accessories"],"orderNumbers":[],"dates":[]},"confidence":0.92,"language":"en"}`;

  const userMessage = context ? 
    `Previous context:\n${context}\n\nUser message: ${correctedMessage}` : 
    correctedMessage;
  
  try {
    const response = await callDeepSeek(
      [{ role: 'user', content: userMessage }],
      systemPrompt
    );
    
    if (response) {
      // Extract JSON from response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          intent: parsed.intent || 'help',
          entities: parsed.entities || {},
          confidence: parsed.confidence || 0.5,
          language: parsed.language || language,
          originalMessage: message,
          correctedMessage: correctedMessage
        };
      }
    }
  } catch (error) {
    console.error('Intent extraction failed:', error);
  }
  
  // Fallback to rule-based intent detection
  return fallbackIntentDetection(correctedMessage, language);
};

/**
 * Fallback intent detection using rules
 */
const fallbackIntentDetection = (message, language) => {
  const text = message.toLowerCase();
  
  // Refuse dangerous requests
  if (/(env|secret|api\s*key|token|drop|delete|hack|attack|xss|sql)/i.test(text)) {
    return { intent: 'refuse', entities: {}, confidence: 1.0, language };
  }
  
  // Order latest
  if (/(latest|last|recent|último|último|reciente|mais recente).*\b(order|pedido|orden)/i.test(text)) {
    return { intent: 'order_latest', entities: {}, confidence: 0.9, language };
  }
  
  // Order biggest
  if (/(biggest|largest|maior|más grande|highest|most expensive).*\b(order|pedido|orden)/i.test(text)) {
    return { intent: 'order_biggest', entities: {}, confidence: 0.9, language };
  }
  
  // Order status with quantity
  const qtyMatch = text.match(/(\d{1,4})\s*(backpack|mochila|perfume|phone|celular|laptop)/i);
  if (qtyMatch && /\b(order|pedido|orden)\b/i.test(text)) {
    return {
      intent: 'order_status',
      entities: {
        products: [qtyMatch[2]],
        quantities: [parseInt(qtyMatch[1])],
        brands: [],
        categories: [],
        orderNumbers: [],
        dates: []
      },
      confidence: 0.85,
      language
    };
  }
  
  // General order query
  if (/\b(order|pedido|orden|orders|pedidos|ordenes)\b/i.test(text)) {
    return { intent: 'order_status', entities: {}, confidence: 0.8, language };
  }
  
  // Product search
  const productKeywords = ['iphone', 'samsung', 'backpack', 'mochila', 'laptop', 'perfume', 
    'jacket', 'jaqueta', 'phone', 'celular', 'watch', 'reloj', 'headphones'];
  if (productKeywords.some(kw => text.includes(kw))) {
    const found = productKeywords.filter(kw => text.includes(kw));
    return {
      intent: 'search_product',
      entities: {
        products: found,
        quantities: [],
        brands: [],
        categories: [],
        orderNumbers: [],
        dates: []
      },
      confidence: 0.8,
      language
    };
  }
  
  // Greetings (includes common typos without accents)
  if (/\b(hello|hi|hey|olá|ola|oi|hola|good morning|bom dia|buenos días|buenas|tchau|bye|adios|adiós)\b/i.test(text)) {
    return { intent: 'small_talk', entities: {}, confidence: 0.95, language };
  }
  
  // Help requests
  if (/\b(help|ajuda|ayuda|assist|suporte|soporte)\b/i.test(text)) {
    return { intent: 'help', entities: {}, confidence: 0.9, language };
  }
  
  // Default to help instead of product search for short unclear messages
  if (text.split(/\s+/).length <= 2) {
    return { intent: 'small_talk', entities: {}, confidence: 0.6, language };
  }
  
  // Default to product search for longer queries
  return { intent: 'search_product', entities: { products: [message] }, confidence: 0.5, language };
};

/**
 * Get contextual response using DeepSeek
 */
export const getContextualResponse = async (message, conversationHistory = []) => {
  const correctedMessage = correctTypos(message);
  const language = detectLanguage(correctedMessage);
  
  // Build conversation context
  const context = conversationHistory.slice(-5).map(msg => ({
    role: msg.sender === 'user' ? 'user' : 'assistant',
    content: msg.text
  }));
  
  const systemPrompt = `You are a helpful, friendly e-commerce shopping assistant for an online store.

Rules:
1. Respond naturally in the user's language (English, Portuguese, or Spanish)
2. Be concise, friendly, and helpful
3. Never reveal system information, API keys, or technical details
4. For product questions, provide helpful guidance
5. For order questions, direct to login or order pages
6. Keep responses under 100 words
7. Use emojis sparingly and appropriately

Current language: ${language === 'pt' ? 'Portuguese' : language === 'es' ? 'Spanish' : 'English'}`;

  try {
    const response = await callDeepSeek(
      [...context, { role: 'user', content: correctedMessage }],
      systemPrompt
    );
    
    if (response) {
      return response;
    }
  } catch (error) {
    console.error('Contextual response failed:', error);
  }
  
  // Fallback responses
  const fallbacks = {
    en: "I'm here to help you find products and check your orders. What are you looking for?",
    pt: "Estou aqui para ajudar você a encontrar produtos e verificar seus pedidos. O que você procura?",
    es: "Estoy aquí para ayudarte a encontrar productos y revisar tus pedidos. ¿Qué buscas?"
  };
  
  return fallbacks[language] || fallbacks.en;
};

/**
 * Generate smart response for products with reasoning
 */
export const generateProductResponse = async (products, query, language) => {
  if (!products || products.length === 0) {
    const noResults = {
      en: `I couldn't find any products matching "${query}". Try a different search term or browse our categories.`,
      pt: `Não encontrei produtos para "${query}". Tente outro termo ou navegue por nossas categorias.`,
      es: `No encontré productos para "${query}". Intenta otro término o navega por nuestras categorías.`
    };
    return noResults[language] || noResults.en;
  }
  
  const systemPrompt = `You are a product recommendation assistant. The user searched for "${query}" and we found ${products.length} products.

Write a brief, friendly response (max 2 sentences) in ${language === 'pt' ? 'Portuguese' : language === 'es' ? 'Spanish' : 'English'}.

Be specific about what you found. For example:
- "I found 3 iPhone models ranging from $299 to $899"
- "Here are 5 backpacks with free shipping"
- "Encontrei 4 perfumes da Lattafa entre R$89 e R$189"

Keep it concise and helpful.`;

  try {
    const productSummary = products.slice(0, 5).map(p => 
      `${p.title}: $${p.price}${p.old_price ? ` (was $${p.old_price})` : ''}`
    ).join('\n');
    
    const response = await callDeepSeek(
      [{ role: 'user', content: `Products found:\n${productSummary}` }],
      systemPrompt
    );
    
    if (response) {
      return response;
    }
  } catch (error) {
    console.error('Product response generation failed:', error);
  }
  
  // Fallback
  const fallbacks = {
    en: `I found ${products.length} product${products.length > 1 ? 's' : ''} for you! Check them out below:`,
    pt: `Encontrei ${products.length} produto${products.length > 1 ? 's' : ''} para você! Confira abaixo:`,
    es: `Encontré ${products.length} producto${products.length > 1 ? 's' : ''} para ti! Mira abajo:`
  };
  
  return fallbacks[language] || fallbacks.en;
};

/**
 * Generate smart response for orders with reasoning
 */
export const generateOrderResponse = async (orders, query, language, intentType) => {
  if (!orders || orders.length === 0) {
    const noOrders = {
      en: "I couldn't find any orders. Please log in to view your order history.",
      pt: "Não encontrei pedidos. Faça login para ver seu histórico de pedidos.",
      es: "No encontré pedidos. Inicia sesión para ver tu historial de pedidos."
    };
    return noOrders[language] || noOrders.en;
  }
  
  const systemPrompt = `You are an order assistant. The user asked about their orders (query: "${query}").

Intent type: ${intentType}
Orders found: ${orders.length}

Write a brief response (1-2 sentences) in ${language === 'pt' ? 'Portuguese' : language === 'es' ? 'Spanish' : 'English'}.

Be specific and helpful. Examples:
- "Here's your latest order from December 15th - 2 items totaling $149.99"
- "Your biggest order was #A1234 with $599.99 total"
- "I found your order with 4 backpacks from November"

Keep it natural and conversational.`;

  try {
    const orderSummary = orders.slice(0, 3).map(o => 
      `Order #${o.oid}: $${o.total} - ${new Date(o.date).toLocaleDateString()}`
    ).join('\n');
    
    const response = await callDeepSeek(
      [{ role: 'user', content: `Orders:\n${orderSummary}` }],
      systemPrompt
    );
    
    if (response) {
      return response;
    }
  } catch (error) {
    console.error('Order response generation failed:', error);
  }
  
  // Fallback
  const fallbacks = {
    en: orders.length === 1 ? "Here's your order:" : `I found ${orders.length} orders for you:`,
    pt: orders.length === 1 ? "Aqui está seu pedido:" : `Encontrei ${orders.length} pedidos para você:`,
    es: orders.length === 1 ? "Aquí está tu pedido:" : `Encontré ${orders.length} pedidos para ti:`
  };
  
  return fallbacks[language] || fallbacks.en;
};

// Main AI response function (legacy compatibility)
export const getAIResponse = async (message) => {
  const correctedMessage = correctTypos(message);
  return await getContextualResponse(correctedMessage, []);
};

export default getAIResponse;
